/**
 * @fileoverview Home Carousel Controller Module.
 * Manages the initialization and configuration of the Glide.js carousel on the home page.
 * Adjusts carousel behavior based on window resizing for a responsive design.
 */

import Glide from '@glidejs/glide'
import "@glidejs/glide/src/assets/sass/glide.core.scss";

// Optional Theme Stylesheet
import "@glidejs/glide/src/assets/sass/glide.theme.scss";
import { elementExists } from "../Utils";

const HomeCarouselController = {
    carouselSelector: '.home-carousel-container',

    /**
     * Initializes the home page carousel if the container element exists.
     */
    init() {
        if (elementExists(this.carouselSelector)) {
            this.startGlide();
        }
    },

    /**
     * Sets event listeners for carousel elements. (Currently not implemented)
     */
    setListeners() {
        document.querySelectorAll(this.dropdownSelector).forEach(element => {
            element.addEventListener('click', this.toggleDropdown.bind(this));
        });
        window.onclick = (event) => this.closeDropdown(event);
    },

    /**
     * Starts the Glide.js carousel and configures responsive behavior.
     */
    startGlide() {
        new Glide('.glide').mount()

        document.addEventListener('DOMContentLoaded', (event) => {
            const glide = new Glide('.glide');
            glide.on(['mount.after', 'run.after'], function () {
                let currentSlide = (glide.index + 1).toString().padStart(2, '0');
                let totalSlides = document.querySelectorAll('.save-js .glide__slide').length;
                totalSlides = totalSlides.toString().padStart(2, '0');
                document.querySelector('.current-slide').textContent = currentSlide;
                document.querySelector('.total-slides').textContent = totalSlides;
                const cubeSize = computeCubeSize();
                setCubeSize(cubeSize);
            });
            glide.mount();
        });

        /**
         * Debounces a function to limit the rate at which it can fire.
         * @param {Function} func - Function to debounce.
         * @param {number} wait - Milliseconds to delay.
         * @returns {Function} Debounced function.
         */
        function debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        }

        /**
         * Sets the size of the carousel's cube elements.
         * @param {number} size - New size for the cubes.
         */
        const setCubeSize = (size) => {
            const containers = document.querySelectorAll('.slide-image-container');
            containers.forEach(function (container) {
                container.style.width = size + 'px';
                container.style.height = size + 'px';
            });
        }


        /**
         * Computes the size of the carousel's cubes based on the window width.
         * @returns {number} Computed size for the cubes.
         */
        const computeCubeSize = () => {
            const width = window.innerWidth;
            if (width <= 1285 && width > 980) {
                return 500;
            }
            else if (width <= 980 && width > 947) {
                return 475;
            }
            else if (width <= 947 && width > 707) {
                return 647;
            }
            else if (width <= 707 && width > 600) {
                return 550;
            }
            else if (width <= 600 && width > 420) {
                return 350;
            }
            else if (width <= 420) {
                return 300;
            } else {
                return 647;
            }
        }

        /**
         * Debounced resize event handler to adjust the size of carousel cubes.
         * This function ensures that the carousel's cube size is recalculated and set whenever
         * the window is resized, but it limits the rate at which the resize handling function is executed.
         * This improves performance by avoiding excessive recalculations and DOM manipulations
         * during rapid or frequent resize events.
         */
        const handleResize = debounce(function () {
            // Get the new window dimensions
            const cubeSize = computeCubeSize();
            setCubeSize(cubeSize);

        }, 100);

        window.addEventListener('resize', handleResize);

    }


}

export default HomeCarouselController;
